<template lang="pug">
.reset-password-wrapper.auth-flow
  .reset-password-container
    h1 {{ $t('onboarding.resetPassword') }}
    h4.description {{ $t('onboarding.resetPasswordDescription') }}
    .reset-password
      .success-message(v-if='showSuccessMessage')
        SVGRenderer(:icon='checkmark', :stroke-color='"var(--primary-color)"', height='32px', width='43px')
        h5 {{ $t('onboarding.resetEmailSent') }}
      form.request-reset-instructions(@submit.prevent='triggerResetPassword', v-else)
        h3.email-title Email
        input#email.h3(
          :class='{ "has-error": emailErrors[0] }',
          @blur='$v.email.$touch()',
          @input='$v.email.$touch()',
          placeholder='yourname@email.com',
          required,
          type='email',
          v-model='email',
          autocomplete='email'
        )
        h5.error(v-if='emailErrors.length') {{ emailErrors[0] }}
        KetchUpButton.primary(
          :class='{ "default-academy": isKetchUpCompany }',
          :disabled='requestingResetPassword || $v.$invalid || !email'
        )
          LoadingSpinner(v-if='requestingResetPassword')
          template(v-else)
            h4 {{ $t('onboarding.resetPassword') }}
            SVGRenderer(
              :icon='arrowRight',
              :stroke-color='"var(--primary-foreground-color)"',
              width='15px',
              height='15px'
            )
      .left-column-ctas
        router-link.ketch-underline(:to='{ name: "Login" }') {{ $t('onboarding.toLogin') }}
</template>

<script setup lang="ts">
  import { ref, watch, computed } from 'vue'

  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'

  import { useVuelidate } from '@vuelidate/core'
  import useIcons from '@/composables/useIcons'
  import useAuthentication from '@/composables/useAuthentication'
  import useI18n from '@/composables/useI18n'
  import useCommonMixin from '@/composables/useCommonMixin'

  import type { ValidationArgs } from '@vuelidate/core'

  import { required, email as emailValidator } from '@vuelidate/validators'

  const { arrowRight, checkmark } = useIcons()
  const { requestingResetPassword, requestResetPassword } = useAuthentication()
  const { translateString } = useI18n()
  const { isKetchUpCompany } = useCommonMixin()

  const email = ref('')
  const showSuccessMessage = ref(false)

  const validation: ValidationArgs<any> = {
    email: {
      required,
      email: emailValidator,
    },
  }

  const $v = useVuelidate(validation, { email })

  const emailErrors = computed((): string[] => {
    const errors: string[] = []
    if (!$v.value.email.$dirty) return errors
    if ($v.value.email.required.$invalid) errors.push(translateString('onboarding.errors.requiredField'))
    else if ($v.value.email.email.$invalid) errors.push(translateString('onboarding.errors.invalidEmail'))
    return errors
  })

  const triggerResetPassword = () => {
    $v.value.$touch()
    if (!$v.value.$invalid) {
      requestResetPassword(email.value)
    }
  }

  watch(requestingResetPassword, (value: boolean) => {
    if (!value) {
      showSuccessMessage.value = true
    }
  })
</script>

<style lang="postcss" scoped>
  .reset-password-wrapper {
    .reset-password-container {
      @apply ketch-w-full sm:ketch-w-fit;

      .reset-password {
        @apply ketch-flex ketch-flex-col md:ketch-flex-row;

        .success-message {
          @apply ketch-flex ketch-flex-col ketch-items-center ketch-space-y-c25 ketch-pt-[27px];

          h5 {
            @apply ketch-max-w-[265px];
          }
        }

        .request-reset-instructions {
          @apply ketch-flex ketch-flex-col md:ketch-w-[300px];

          .email-title {
            @apply ketch-font-bold;
          }

          input {
            @apply ketch-border ketch-px-c15 ketch-py-c10 ketch-h-c45 ketch-outline-none ketch-mt-c5;
            @apply ketch-rounded-normal focus:ketch-border-primary-color;

            &.has-error {
              @apply ketch-border-red-500;
            }
          }

          .error {
            @apply ketch-text-red-500;
          }

          button {
            @apply ketch-w-full ketch-justify-between ketch-mt-c15 ketch-rounded-normal;

            .loading {
              @apply ketch-mx-auto;
            }

            &.default-academy {
              @apply ketch-bg-black;
            }
          }
        }
      }
    }
  }
</style>
